import React, { useState, useEffect } from 'react';
import {
  Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Toolbar, AppBar, Typography, IconButton, Divider, Avatar,
  Tooltip, Chip, useMediaQuery, useTheme, Menu, MenuItem, Alert,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InventoryIcon from '@mui/icons-material/Inventory2';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PeopleIcon from '@mui/icons-material/People';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SpeedIcon from '@mui/icons-material/Speed';
import LogoutIcon from '@mui/icons-material/Logout';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import ExtensionIcon from '@mui/icons-material/Extension';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PersonIcon from '@mui/icons-material/Person';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate, useLocation } from 'react-router-dom';
import { tenantApi } from '../../api/tenantApi';
import { useAuthStore } from '../../store/authStore';
import { useReadOnly } from '../../context/ReadOnlyContext';

const DRAWER_WIDTH = 240;

// Nav items — `featureKey` is optional; if set, the item is hidden if disabled
const NAV_ITEMS = [
  { label: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { label: 'Inventory', icon: <InventoryIcon />, path: '/inventory' },
  { label: 'Warehouses', icon: <WarehouseIcon />, path: '/warehouses', featureKey: 'warehousing.multi_warehouse' },
  { label: 'Reports', icon: <AssessmentIcon />, path: '/reports', featureKey: 'reporting.export_csv' },
  { label: 'API Keys', icon: <VpnKeyIcon />, path: '/api-keys', featureKey: 'api_management.api_key_self_service' },
  { label: 'Users', icon: <PeopleIcon />, path: '/users' },
  { label: 'Usage & Limits', icon: <SpeedIcon />,     path: '/usage',    featureKey: 'licensing.usage_dashboard' },
  { label: 'Notifications', icon: <NotificationsIcon />, path: '/notifications' },
  { label: 'Features & Add-Ons', icon: <ExtensionIcon />, path: '/add-ons' },
];

export default function AppLayout({ children, onSignOut, user }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [features, setFeatures] = useState({});
  const [menuAnchor, setMenuAnchor] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantName, plan, setTenantMeta } = useAuthStore();
  const { isReadOnly } = useReadOnly();

  useEffect(() => {
    tenantApi.getMyFeatures().then(setFeatures).catch(() => {});
    // Refresh tenant name + plan from the live /me endpoint so an admin's
    // plan change (trial → paid) is reflected without requiring re-login.
    // The JWT `custom:plan` claim is only set at login and never updated.
    tenantApi.getMyProfile().then((me) => {
      const livePlan = me?.subscription?.plan || me?.tenant?.plan || null;
      const liveName = me?.tenant?.name || null;
      if (livePlan || liveName) { setTenantMeta(liveName, livePlan); }
    }).catch(() => {});
  }, [setTenantMeta]);

  const visibleNavItems = NAV_ITEMS.filter(
    (item) => !item.featureKey || features[item.featureKey] !== false
  );

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar>
        <Box>
          <Typography variant="h6" noWrap fontWeight={700}>
            Inventory
          </Typography>
          {tenantName && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {tenantName}
            </Typography>
          )}
          {plan && (
            <Chip label={plan.toUpperCase()} size="small" color="primary" sx={{ ml: 1, fontSize: 10 }} />
          )}
        </Box>
      </Toolbar>
      <Divider />
      <List sx={{ flex: 1, pt: 1 }}>
        {visibleNavItems.map((item) => (
          <ListItemButton
            key={item.path}
            selected={location.pathname.startsWith(item.path)}
            onClick={() => { navigate(item.path); if (isMobile) { setDrawerOpen(false); } }}
            sx={{ borderRadius: 1, mx: 1, mb: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14 }} />
          </ListItemButton>
        ))}
      </List>
      <Divider />
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
          {user?.signInDetails?.loginId?.[0]?.toUpperCase() || '?'}
        </Avatar>
        <Box flex={1} minWidth={0}>
          <Typography variant="caption" noWrap display="block" fontWeight={500}>
            {user?.signInDetails?.loginId}
          </Typography>
        </Box>
        <Tooltip title="More options">
          <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <MenuItem onClick={() => { setMenuAnchor(null); navigate('/settings'); }}>
            <PersonIcon fontSize="small" sx={{ mr: 1 }} /> Profile
          </MenuItem>
        </Menu>
        <Tooltip title="Sign out">
          <IconButton size="small" onClick={onSignOut}><LogoutIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          display: { md: 'none' },
          zIndex: (t) => t.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={() => setDrawerOpen((o) => !o)}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ ml: 1 }}>Inventory Platform</Typography>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? drawerOpen : true}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
      >
        {drawer}
      </Drawer>

      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          mt: { xs: 8, md: 0 },
          bgcolor: 'grey.50',
          minHeight: '100vh',
        }}
      >
        {isReadOnly && (
          <Alert
            severity="warning"
            icon={<LockIcon />}
            sx={{ borderRadius: 0, fontWeight: 500 }}
          >
            Your account is locked. You have read-only access. You may still export your inventory data.
            Contact support to upgrade your plan.
          </Alert>
        )}
        {children}
      </Box>
    </Box>
  );
}
